import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loadConfig } from '../store/configSlice';
import type { InterviewConfig, InterviewerConfig } from '../types';
import { jobCategories, LEVELS, INTERVIEW_TYPES } from '../data/jobCategories';
import { auth } from '../services/auth';
import AuthModal from './AuthModal';
import {
  Code2Icon,
  PaletteIcon,
  UsersIcon,
  CalculatorIcon,
  TrendingUpIcon,
  SettingsIcon,
  UserPlusIcon,
  Building2Icon,
  ScaleIcon,
  HeadphonesIcon,
  SparklesIcon,
  BriefcaseIcon,
  AwardIcon,
  FileTextIcon,
  ZapIcon,
  CamcorderIcon,
  MicIcon,
} from './icons/JobIcons';

interface InterviewSetupProps {
  onStart: (config: InterviewConfig) => void;
}

// 图标映射表
const iconComponents: Record<
  string,
  React.FC<{ className?: string; size?: number; color?: string }>
> = {
  Code2: Code2Icon,
  Palette: PaletteIcon,
  Users: UsersIcon,
  Calculator: CalculatorIcon,
  TrendingUp: TrendingUpIcon,
  Settings: SettingsIcon,
  UserPlus: UserPlusIcon,
  Building2: Building2Icon,
  Scale: ScaleIcon,
  Headphones: HeadphonesIcon,
};

// 特性数据
const features = [
  {
    icon: MicIcon,
    title: 'AI 语音对练',
    description:
      '真人口语级语音交互，告别打字面试。像跟真人面试官对话一样自然，沉浸式体验拉满',
  },
  {
    icon: CamcorderIcon,
    title: 'AI 行为分析',
    description:
      '摄像头实时捕捉微表情与身体姿态，智能分析自信度、眼神交流等表现，帮你打磨面试第一印象',
  },
  {
    icon: SparklesIcon,
    title: 'AI 智能评估',
    description:
      '大模型深度分析面试表现，从技术、逻辑、表达等多维度生成专业评估报告，精准定位提升方向',
  },
  {
    icon: FileTextIcon,
    title: 'AI 简历中心',
    description:
      'AI 一键生成专业简历、智能优化内容、自动评估竞争力，让你的简历在海量投递中脱颖而出',
  },
];

export default function InterviewSetup({ onStart }: InterviewSetupProps) {
  const dispatch = useAppDispatch();
  const configData = useAppSelector((state) => state.config.data);

  // 从配置数据中获取面试官列表（只显示启用的）
  const interviewers = useMemo(() => {
    if (!configData?.interviewers) return [];
    return configData.interviewers.filter((i) => i.status === 1);
  }, [configData]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedInterviewer, setSelectedInterviewer] =
    useState<InterviewerConfig | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // 性别筛选状态，默认选择男性（1）
  const [genderFilter, setGenderFilter] = useState<number>(1);

  // 根据性别筛选后的面试官列表
  const filteredInterviewers = useMemo(() => {
    return interviewers.filter((i) => i.gender === genderFilter);
  }, [interviewers, genderFilter]);

  // 压力面试状态，默认关闭
  const [isPressureInterview, setIsPressureInterview] =
    useState<boolean>(false);

  // 岗位JD状态，默认关闭
  const [hasJobDescription, setHasJobDescription] = useState<boolean>(false);
  const [jobDescription, setJobDescription] = useState<string>('');

  // 登录弹窗状态
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // 组件加载时获取配置（每次进入首页都重新请求）
  useEffect(() => {
    dispatch(loadConfig());
  }, [dispatch]);

  const currentCategory = useMemo(
    () => jobCategories.find((cat) => cat.id === selectedCategory),
    [selectedCategory],
  );

  // 实际使用的面试官（仅使用用户选中的）
  const currentInterviewer = selectedInterviewer;

  // 获取阿凯面试官信息（用于压力面试模式）
  const akaiInterviewer = useMemo(() => {
    return interviewers.find((i) => i.name === '阿凯' && i.gender === 1);
  }, [interviewers]);

  const handleCategorySelect = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setIsExpanded(!isExpanded);
    } else {
      setSelectedCategory(categoryId);
      setSelectedPosition('');
      setIsExpanded(true);
    }
  };

  const handleStart = () => {
    if (!selectedPosition || !selectedCategory) return;

    // 检查用户是否已登录
    const currentUser = auth.getUser();
    if (!currentUser) {
      // 未登录，弹出登录弹窗
      setIsAuthModalOpen(true);
      return;
    }

    // 根据是否压力面试确定面试官
    let finalInterviewer = currentInterviewer;
    if (isPressureInterview) {
      // 查找名为"阿凯"的男性面试官
      const akaiInterviewer = interviewers.find(
        (i) => i.name === '阿凯' && i.gender === 1,
      );
      if (akaiInterviewer) {
        finalInterviewer = akaiInterviewer;
      }
    }

    if (!finalInterviewer) return;

    onStart({
      jobType: selectedPosition,
      jobCategory: selectedCategory,
      level: selectedLevel as '初级' | '中级' | '高级',
      type: selectedType as '技术面试' | '行为面试' | '综合面试',
      interviewer: finalInterviewer,
      isPressureInterview,
      hasJobDescription,
      jobDescription: hasJobDescription ? jobDescription : undefined,
      userInfo: {
        nickname: currentUser.nickname,
        techStacks: currentUser.tech_stacks,
      },
    });
  };

  const handleAuthModalClose = () => {
    setIsAuthModalOpen(false);
    // 如果登录成功，继续开始面试
    const currentUser = auth.getUser();
    if (currentUser && selectedPosition && selectedCategory) {
      // 根据是否压力面试确定面试官
      let finalInterviewer = currentInterviewer;
      if (isPressureInterview) {
        const akaiInterviewer = interviewers.find(
          (i) => i.name === '阿凯' && i.gender === 1,
        );
        if (akaiInterviewer) {
          finalInterviewer = akaiInterviewer;
        }
      }

      if (!finalInterviewer) return;

      onStart({
        jobType: selectedPosition,
        jobCategory: selectedCategory,
        level: selectedLevel as '初级' | '中级' | '高级',
        type: selectedType as '技术面试' | '行为面试' | '综合面试',
        interviewer: finalInterviewer,
        isPressureInterview,
        hasJobDescription,
        jobDescription: hasJobDescription ? jobDescription : undefined,
        userInfo: {
          nickname: currentUser.nickname,
          techStacks: currentUser.tech_stacks,
        },
      });
    }
  };

  const canStart =
    selectedPosition &&
    selectedCategory &&
    selectedLevel &&
    selectedType &&
    (isPressureInterview || currentInterviewer);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] relative overflow-hidden">
      {/* 背景装饰 - 琥珀金暖色调 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-amber-500/8 rounded-full blur-[120px] animate-pulse" />
        <div
          className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-emerald-500/8 rounded-full blur-[100px] animate-pulse"
          style={{ animationDelay: '2s' }}
        />
        <div className="absolute top-[40%] left-[50%] transform -translate-x-1/2 w-[800px] h-[800px] bg-amber-600/5 rounded-full blur-[150px]" />
      </div>

      {/* 网格背景 */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(245, 158, 11, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245, 158, 11, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-12 min-h-screen flex flex-col">
        {/* Header */}
        <header className="mb-12 animate-fade-in">
          {/* 标题区域 */}
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white via-amber-100 to-amber-200 bg-clip-text text-transparent">
                面面帮
              </span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
              选择目标岗位，开启专业的 AI 面试训练之旅
              <br />
              <span className="text-amber-500">
                10大行业类别 · 100+细分岗位 · 个性化评估报告
              </span>
            </p>

            {/* 演示视频 */}
            {configData?.displayVideoUrl && (
              <div className="max-w-3xl mx-auto">
                <div className="relative rounded-2xl overflow-hidden border border-amber-500/20 shadow-2xl shadow-amber-500/10 bg-[#1E293B]/80 backdrop-blur-sm">
                  <video controls className="w-full aspect-video" poster="">
                    <source
                      src={`${configData.displayVideoUrl}?timestamp=${Date.now()}`}
                      type="video/mp4"
                    />
                    您的浏览器不支持视频播放
                  </video>
                </div>
                <p className="mt-3 text-gray-500 text-sm">
                  观看演示视频，了解面面帮的使用方法
                </p>
              </div>
            )}
          </div>
        </header>

        {/* 主要内容区 */}
        <div className="flex-1 max-w-7xl mx-auto w-full">
          {/* 岗位类别选择 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BriefcaseIcon size={20} color="#F59E0B" />
              选择岗位类别
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {jobCategories.map((category, index) => {
                const IconComponent = iconComponents[category.icon];
                const isSelected = selectedCategory === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`
                      relative group p-6 rounded-2xl border transition-all duration-300 cursor-pointer
                      ${
                        isSelected
                          ? 'bg-gradient-to-br from-amber-500 to-amber-600 border-transparent shadow-lg shadow-amber-500/25'
                          : 'bg-gradient-to-br from-[#1E293B]/90 to-[#334155]/40 border-slate-600/30 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10'
                      }
                    `}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div
                        className={`
                          w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300
                          ${
                            isSelected
                              ? 'bg-white/20'
                              : 'bg-amber-500/10 group-hover:bg-amber-500/20'
                          }
                        `}
                      >
                        {IconComponent && (
                          <IconComponent
                            size={24}
                            color={isSelected ? '#FFFFFF' : category.color}
                          />
                        )}
                      </div>
                      <span
                        className={`font-medium text-sm ${
                          isSelected
                            ? 'text-white'
                            : 'text-slate-300 group-hover:text-white'
                        }`}
                      >
                        {category.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 子岗位选择面板 */}
          {isExpanded && currentCategory && (
            <div className="mb-8 animate-slide-down">
              <div className="bg-[#1E293B]/60 border border-amber-500/15 rounded-2xl p-6 backdrop-blur-sm">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: currentCategory.color }}
                  />
                  {currentCategory.name} - 选择具体岗位
                </h3>
                <div className="flex flex-wrap gap-3">
                  {currentCategory.subPositions.map((position, index) => (
                    <button
                      key={position.id}
                      onClick={() => setSelectedPosition(position.name)}
                      className={`
                        relative px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer
                        animate-fade-in-scale
                        ${
                          selectedPosition === position.name
                            ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                            : 'bg-slate-700/50 text-slate-300 border border-slate-600/30 hover:border-amber-500/40 hover:text-slate-200'
                        }
                      `}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {position.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 级别和面试类型选择 */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* 级别选择 */}
            <div className="bg-[#1E293B]/40 border border-slate-600/20 rounded-2xl p-6">
              <h3 className="text-lg font-medium text-white mb-4">选择级别</h3>
              <div className="flex gap-3">
                {LEVELS.map((level) => (
                  <button
                    key={level}
                    onClick={() => setSelectedLevel(level)}
                    className={`
                    flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200 cursor-pointer
                    ${
                      selectedLevel === level
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                        : 'bg-slate-700/30 text-slate-400 border border-slate-600/30 hover:border-amber-500/40 hover:text-slate-200'
                    }
                  `}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* 面试类型选择 */}
            <div className="bg-[#1E293B]/40 border border-slate-600/20 rounded-2xl p-6">
              <h3 className="text-lg font-medium text-white mb-4">面试类型</h3>
              <div className="flex gap-3">
                {INTERVIEW_TYPES.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`
                    flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200 cursor-pointer
                    ${
                      selectedType === type
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                        : 'bg-slate-700/30 text-slate-400 border border-slate-600/30 hover:border-amber-500/40 hover:text-slate-200'
                    }
                  `}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 岗位JD选项 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <FileTextIcon size={20} color="#F59E0B" />
              是否开启岗位JD
            </h2>
            <div className="bg-[#1E293B]/40 border border-slate-600/20 rounded-2xl p-6">
              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      !hasJobDescription
                        ? 'border-amber-500 bg-amber-500'
                        : 'border-slate-500 group-hover:border-amber-400'
                    }`}
                  >
                    {!hasJobDescription && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="jobDescription"
                    checked={!hasJobDescription}
                    onChange={() => setHasJobDescription(false)}
                    className="sr-only"
                  />
                  <span
                    className={`font-medium ${
                      !hasJobDescription ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    否
                  </span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      hasJobDescription
                        ? 'border-amber-500 bg-amber-500'
                        : 'border-slate-500 group-hover:border-amber-400'
                    }`}
                  >
                    {hasJobDescription && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="jobDescription"
                    checked={hasJobDescription}
                    onChange={() => setHasJobDescription(true)}
                    className="sr-only"
                  />
                  <span
                    className={`font-medium ${
                      hasJobDescription ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    是
                  </span>
                </label>
              </div>
              <p className="mt-3 text-slate-500 text-sm">
                开启后，面试官可以根据岗位JD更针对性地提问和考查
              </p>

              {/* JD输入框 - 仅在开启时显示 */}
              {hasJobDescription && (
                <div className="mt-4">
                  <textarea
                    value={jobDescription}
                    onChange={(e) => {
                      const value = e.target.value;
                      // 限制最多600字，超出部分截取
                      if (value.length <= 600) {
                        setJobDescription(value);
                      } else {
                        setJobDescription(value.slice(0, 600));
                      }
                    }}
                    placeholder="请粘贴或输入岗位JD内容"
                    className="w-full h-48 px-4 py-3 bg-[#0B0F19]/80 border border-slate-600/30 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 resize-none transition-all duration-200"
                  />
                  <p className="mt-2 text-slate-500 text-xs text-right">
                    {jobDescription.length}/600
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 压力面试选项 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <ZapIcon size={20} color="#EF4444" />
              是否开启压力面试
            </h2>
            <div className="bg-[#1E293B]/40 border border-slate-600/20 rounded-2xl p-6">
              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      !isPressureInterview
                        ? 'border-amber-500 bg-amber-500'
                        : 'border-slate-500 group-hover:border-amber-400'
                    }`}
                  >
                    {!isPressureInterview && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="pressureInterview"
                    checked={!isPressureInterview}
                    onChange={() => setIsPressureInterview(false)}
                    className="sr-only"
                  />
                  <span
                    className={`font-medium ${
                      !isPressureInterview ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    否
                  </span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      isPressureInterview
                        ? 'border-red-500 bg-red-500'
                        : 'border-slate-500 group-hover:border-red-400'
                    }`}
                  >
                    {isPressureInterview && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="pressureInterview"
                    checked={isPressureInterview}
                    onChange={() => setIsPressureInterview(true)}
                    className="sr-only"
                  />
                  <span
                    className={`font-medium ${
                      isPressureInterview ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    是
                  </span>
                </label>
              </div>
              <p className="mt-3 text-slate-500 text-sm">
                开启压力面试后，将由专业的高压面试官进行面试，问题更具挑战性，适合想体验高压环境的用户
              </p>
            </div>
          </div>

          {/* 面试官选择 - 仅在非压力面试模式下显示 */}
          {!isPressureInterview && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <UsersIcon size={20} color="#F59E0B" />
                选择面试官
                {/* 性别筛选 */}
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => setGenderFilter(1)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                      genderFilter === 1
                        ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
                        : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-blue-500/30'
                    }`}
                  >
                    男性
                  </button>
                  <button
                    onClick={() => setGenderFilter(0)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                      genderFilter === 0
                        ? 'bg-rose-500/30 text-rose-300 border border-rose-500/50'
                        : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-rose-500/30'
                    }`}
                  >
                    女性
                  </button>
                </div>
              </h2>
              <div className="bg-[#1E293B]/60 border border-amber-500/15 rounded-2xl p-6 backdrop-blur-sm">
                {/* 当前选中的面试官 */}
                {currentInterviewer && (
                  <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/20">
                    <img
                      src={currentInterviewer.avatar}
                      alt={currentInterviewer.name}
                      className="w-16 h-16 rounded-full bg-slate-700"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold text-lg">
                          {currentInterviewer.name}
                        </h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            currentInterviewer.gender === 0
                              ? 'bg-rose-500/25 text-rose-400 border border-rose-500/30'
                              : 'bg-blue-600/40 text-blue-300 border border-blue-500/50'
                          }`}
                        >
                          {currentInterviewer.gender === 0 ? '女性' : '男性'}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        {currentInterviewer.personality || '专业面试官'}
                      </p>
                      <p className="text-amber-400 text-xs mt-1">
                        {currentInterviewer.scenario || '适用于各类岗位面试'}
                      </p>
                    </div>
                  </div>
                )}

                {/* 面试官列表 */}
                <div className="grid grid-cols-4 md:grid-cols-8 gap-3 overflow-visible max-h-[21.8rem] pt-2 scrollbar-none">
                  {filteredInterviewers.map((interviewer) => (
                    <button
                      key={interviewer.id}
                      onClick={() => setSelectedInterviewer(interviewer)}
                      className={`
                        relative p-3 rounded-xl border transition-all duration-200 cursor-pointer
                        ${
                          currentInterviewer?.id === interviewer.id
                            ? 'bg-amber-500/30 border-amber-500/50 shadow-lg shadow-amber-500/20'
                            : 'bg-slate-700/20 border-slate-600/20 hover:border-amber-500/30 hover:bg-amber-500/10'
                        }
                      `}
                    >
                      <img
                        src={interviewer.avatar}
                        alt={interviewer.name}
                        className={`w-full aspect-square rounded-full bg-slate-700 mb-2 transition-transform duration-200 ${
                          currentInterviewer?.id === interviewer.id
                            ? 'scale-110'
                            : 'hover:scale-105'
                        }`}
                      />
                      <p
                        className={`text-xs font-medium text-center truncate ${
                          currentInterviewer?.id === interviewer.id
                            ? 'text-white'
                            : 'text-slate-400'
                        }`}
                      >
                        {interviewer.name}
                      </p>
                      {currentInterviewer?.id === interviewer.id && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 压力面试模式下显示固定面试官信息 */}
          {isPressureInterview && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <UsersIcon size={20} color="#EF4444" />
                压力面试官
              </h2>
              <div className="bg-[#1E293B]/60 border border-red-500/20 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/20">
                  <img
                    src={
                      akaiInterviewer?.avatar ||
                      'https://api.dicebear.com/7.x/avataaars/svg?seed=akai&gender=male'
                    }
                    alt="阿凯"
                    className="w-16 h-16 rounded-full bg-slate-700"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold text-lg">阿凯</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                        男性
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                        高压模式
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      {akaiInterviewer?.personality ||
                        '严厉、直接、高压 - 专业压力面试官'}
                    </p>
                    <p className="text-red-400 text-xs mt-1">
                      {akaiInterviewer?.scenario ||
                        '适用于抗压能力测试和高压场景模拟'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 开始按钮 */}
          <div className="text-center">
            <button
              onClick={handleStart}
              disabled={!canStart}
              className={`
                relative px-12 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 overflow-hidden
                ${
                  canStart
                    ? 'bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] animate-gradient cursor-pointer'
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }
              `}
            >
              <span className="relative z-10 flex items-center gap-2">
                <SparklesIcon size={20} />
                开始面试
              </span>
              {canStart && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />
              )}
            </button>
            {!canStart && (
              <p className="mt-3 text-slate-500 text-sm">
                请先选择岗位类别、具体岗位、级别、面试类型和面试官
              </p>
            )}
          </div>
        </div>

        {/* 特性展示 */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="group p-6 rounded-2xl bg-gradient-to-br from-[#1E293B]/80 to-[#334155]/20 border border-slate-600/20 hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <feature.icon size={28} color="#F59E0B" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">
                {feature.title}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-scale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-fade-in-scale {
          animation: fade-in-scale 0.3s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-down {
          animation: slide-down 0.4s ease-out forwards;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* 登录弹窗 - 未登录时点击开始面试弹出 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleAuthModalClose}
        defaultMode="login"
      />
    </div>
  );
}
